/**
 * Region + subregion config — shared single source of truth with the backend.
 *
 * Reads the same JSON the Python registry uses (backend/app/jurisdictions/regions.json).
 * The selectable region drives only two behaviour profiles: US for the `US`
 * region, UK for everything else. Subregion is metadata only.
 *
 * Conventions (no per-region flags needed):
 * - forum required        -> `forum_enabled`
 * - subregion is a list   -> non-empty `subregions` (strict dropdown)
 * - subregion is free text -> empty `subregions` (optional free-text input)
 * - subregion required     -> non-empty `subregions`
 */

import regionsConfig from '@jurisdictions/regions.json';

export interface RegionConfig {
  name: string;
  subregions: string[];
  forum_enabled: boolean;
}

export interface SubregionConfig {
  options: string[];
  required: boolean;
  freeText: boolean;
}

export const REGIONS: RegionConfig[] = regionsConfig.regions as RegionConfig[];

export function getRegion(name: string | null | undefined): RegionConfig | undefined {
  const n = (name ?? '').trim().toUpperCase();
  return REGIONS.find((r) => r.name.toUpperCase() === n);
}

export function regionRequiresForum(name: string | null | undefined): boolean {
  return getRegion(name)?.forum_enabled ?? false;
}

export function getSubregionConfig(name: string | null | undefined): SubregionConfig {
  const options = getRegion(name)?.subregions ?? [];
  return {
    options,
    required: options.length > 0,
    freeText: options.length === 0,
  };
}

export function isUsRegion(name: string | null | undefined): boolean {
  return (name ?? 'UK').toUpperCase() === 'US';
}
